/* eslint-disable prefer-arrow/prefer-arrow-functions */
/**
 * SAML Bridge Integration
 * Handles SAML authentication via the saml-bridge service using popup flow
 */

import { getToken } from 'firebase/app-check';
import { Auth, signInWithCustomToken, UserCredential } from 'firebase/auth';
import { getTenantAppCheck } from '../../../../data/providers/appsProvider';

interface SAMLAuthResponse {
  type: 'saml_auth';
  token?: string;
  error?: string;
}

interface SAMLInitiateFormData {
  email: string;
  org_id: string;
  tenant_id: string;
  firebase_provider_id: string;
  platform: 'web';
  redirect_uri: string;
  app_check_token?: string;
}

/**
 * Signs in with SAML using popup flow via saml-bridge
 *
 * @param auth - Firebase Auth instance
 * @param email - User's email address
 * @param orgId - Organization ID
 * @param tenantId - Tenant ID (optional)
 * @param firebaseProviderId - Firebase SAML provider ID (e.g., "saml.okta")
 * @param samlBridgeDomain - SAML bridge service domain from identity service
 * @returns UserCredential after successful authentication
 */
export async function signInWithSAMLPopup(
  auth: Auth,
  email: string,
  orgId: string,
  tenantId: string | null,
  firebaseProviderId: string,
  samlBridgeDomain: string
): Promise<UserCredential> {
  const callbackUrl = `${window.location.origin}/auth/saml/callback`;

  // Open popup window
  const popup = window.open(
    'about:blank',
    'saml_auth',
    'width=500,height=700,scrollbars=yes,resizable=yes'
  );

  if (!popup) {
    throw new Error(
      'Popup blocked. Please allow popups for this site and try again.'
    );
  }

  try {
    // Get App Check token from the tenant-specific Firebase app
    let appCheckToken: string | undefined;

    // The tenant app's AppCheck should already be initialized by getAuthForTenant()
    const tenantAppCheck = getTenantAppCheck();
    if (tenantAppCheck) {
      try {
        const tokenResult = await getToken(tenantAppCheck, false);
        appCheckToken = tokenResult.token;
      } catch (e) {
        // Continue without App Check token - backend will reject if required
      }
    }

    // Build form data with App Check token
    const formData: SAMLInitiateFormData = {
      email,
      org_id: orgId,
      tenant_id: tenantId || '',
      firebase_provider_id: firebaseProviderId,
      platform: 'web',
      redirect_uri: callbackUrl,
    };

    // Add App Check token if we got one
    if (appCheckToken) {
      formData.app_check_token = appCheckToken;
    }

    // Create form in parent window, then move to popup
    // This avoids cross-origin issues
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `https://${samlBridgeDomain}/initiate`;
    form.target = 'saml_auth'; // Target the popup window
    form.style.display = 'none';

    // Add form fields
    Object.entries(formData).forEach(
      ([key, value]: [string, string | undefined]) => {
        if (value !== undefined && value !== '') {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = value;
          form.appendChild(input);
        }
      }
    );

    // Add form to parent window, submit it, then remove it
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);

    // Wait for popup to send token back
    const token = await waitForPopupMessage(popup);

    // Sign in with custom token
    return await signInWithCustomToken(auth, token);
  } catch (error) {
    popup?.close();
    throw error;
  }
}

/**
 * Waits for the popup to post a message with the authentication result
 */
function waitForPopupMessage(popup: Window): Promise<string> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      cleanup();
      reject(
        new Error(
          'SAML authentication timeout. Please try again or contact your administrator if the problem persists.'
        )
      );
    }, 5 * 60 * 1000); // 5 minute timeout

    const messageHandler = (event: MessageEvent<SAMLAuthResponse>) => {
      // Validate origin - must be from our domain
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data?.type === 'saml_auth') {
        cleanup();

        if (event.data.error) {
          reject(new Error(event.data.error));
        } else if (event.data.token) {
          resolve(event.data.token);
        } else {
          reject(
            new Error('No authentication token received from SAML provider')
          );
        }
      }
    };

    const cleanup = () => {
      clearTimeout(timeout);
      window.removeEventListener('message', messageHandler);
      popup?.close();
    };

    window.addEventListener('message', messageHandler);
  });
}
